import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetMyRequestForQuoteFilterOptions,
  useGetMyRequestForQuotes,
  type MyRequestForQuotesParams,
} from '~/api/requestForQuote';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { TabList, type TabDescriptor } from '~/components/ui/TabList';
import { tabButtonId, tabPanelId } from '~/components/ui/tabIds';
import { PerspectiveCaption } from '~/components/ui/PerspectiveCaption';
import type { MyRequestForQuotesQuery } from '~/graphql/graphql';
import {
  activeRowCount,
  archivedOf,
  buildRequestForQuoteFiltersInput,
  withoutArchivedRows,
} from '~/pages/my-requests-for-quote/lib/requestForQuoteFilters';
import { newArchivedIncludeRow } from '~/pages/my-requests-for-quote/lib/requestForQuoteFilterFields';
import { useRequestForQuoteSavedViews } from '~/pages/my-requests-for-quote/lib/useRequestForQuoteSavedViews';
import { requestForQuoteEmptyStateKey } from '~/pages/my-requests-for-quote/lib/requestForQuoteEmptyState';
import { MyRequestForQuotesList } from '~/pages/my-requests-for-quote/sections/MyRequestForQuotesList';
import type { MyRequestForQuoteRow } from '~/pages/my-requests-for-quote/sections/MyRequestForQuotesList';
import { RequestForQuoteToolbar } from '~/pages/my-requests-for-quote/sections/RequestForQuoteToolbar';
import { formatNameWithCode, sellerLabel } from '~/utils';

type Tab = 'pending' | 'quotes';

const tabOrder: Tab[] = ['pending', 'quotes'];

export default function MyRequestForQuotes() {
  const { t } = useTranslation('my-requests-for-quote');
  const tabsId = useId();
  const [activeTab, setActiveTab] = useState<Tab>('pending');
  const views = useRequestForQuoteSavedViews();
  const isWaitingForFilters = views.isLoadingViews && !views.isViewsError;
  const { data: filterOptionsData, isLoading: isLoadingFilterOptions } =
    useGetMyRequestForQuoteFilterOptions();
  const filterOptions = filterOptionsData?.myRequestForQuoteFilterOptions;

  const productOptions = useMemo(
    () =>
      (filterOptions?.products ?? []).map((product) => ({
        label: formatNameWithCode(product.name, product.code),
        value: product.id,
      })),
    [filterOptions?.products]
  );

  const sellerOptions = useMemo(
    () =>
      (filterOptions?.sellers ?? []).map((seller) => ({
        label: sellerLabel(seller),
        value: seller.id,
      })),
    [filterOptions?.sellers]
  );

  const creatorOptions = useMemo(
    () =>
      (filterOptions?.creators ?? []).map((creator) => ({
        label: creator.name,
        value: creator.id,
      })),
    [filterOptions?.creators]
  );

  const hasArchivedRequests = filterOptions?.hasArchivedRequests ?? false;

  const isQuotesTab = activeTab === 'quotes';
  const applicableRows = isQuotesTab
    ? views.rows
    : withoutArchivedRows(views.rows);
  const areArchivedRequestsHidden =
    hasArchivedRequests && isQuotesTab && archivedOf(views.rows) === undefined;
  const emptyStateKey = requestForQuoteEmptyStateKey({
    isQuotesTab,
    isFiltered: activeRowCount(applicableRows) > 0,
    areArchivedRequestsHidden,
    hasSentRequests: filterOptions?.hasSentRequests,
  });

  const queryParams = useMemo<MyRequestForQuotesParams>(
    () => ({
      needsResponse: activeTab === 'pending',
      archived: isQuotesTab ? archivedOf(views.rows) : undefined,
      filters: buildRequestForQuoteFiltersInput(views.rows),
    }),
    [activeTab, isQuotesTab, views.rows]
  );

  const list = usePaginatedQuery<
    MyRequestForQuotesQuery,
    MyRequestForQuoteRow,
    MyRequestForQuotesParams
  >({
    fetchHook: useGetMyRequestForQuotes,
    select: (data) => data?.myRequestForQuotes,
    options: { filters: queryParams, enabled: !isWaitingForFilters },
  });

  const tabLabels: Record<Tab, string> = {
    pending: t('my_requests_for_quote.tabs.pending'),
    quotes: t('my_requests_for_quote.tabs.quotes'),
  };

  const tabs: TabDescriptor<Tab>[] = tabOrder.map((tab) => ({
    id: tab,
    label: tabLabels[tab],
    count: tab === activeTab ? list.pagination?.totalElements : undefined,
    testId: `my-request-for-quote-tab-${tab}`,
  }));

  return (
    <div className="w-full flex flex-col gap-8">
      <title>{t('my_requests_for_quote.title')}</title>
      <div>
        <h1 className="text-2xl font-semibold text-neut-prim">
          {t('my_requests_for_quote.title')}
        </h1>
        <PerspectiveCaption perspective="BUYING" />
      </div>

      <TabList
        baseId={tabsId}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        ariaLabel={t('my_requests_for_quote.tabs_label')}
      />

      <div
        role="tabpanel"
        id={tabPanelId(tabsId)}
        aria-labelledby={tabButtonId(tabsId, activeTab)}
        className="flex flex-col gap-2"
      >
        <RequestForQuoteToolbar
          products={productOptions}
          sellers={sellerOptions}
          creators={creatorOptions}
          isLoadingOptions={isLoadingFilterOptions}
          isArchivedFilterAvailable={isQuotesTab}
          views={views}
        />
        <MyRequestForQuotesList
          requestForQuotes={list.data}
          isLoading={
            list.isLoading ||
            isWaitingForFilters ||
            (isQuotesTab && isLoadingFilterOptions && list.data.length === 0)
          }
          isError={Boolean(list.error)}
          onRetry={() => void list.refetch()}
          sentinelRef={list.sentinelRef}
          isLoadingMore={list.isLoadingMore}
          hasMoreData={list.hasMoreData}
          totalCount={list.pagination?.totalElements ?? 0}
          emptyStateKey={emptyStateKey}
          onShowArchivedRequests={
            areArchivedRequestsHidden
              ? () => views.setRows([...views.rows, newArchivedIncludeRow()])
              : undefined
          }
        />
      </div>
    </div>
  );
}
